import React from 'react';
import _merge from 'lodash/merge';
import { getContext } from 'common';
import { ExpansionPanelContext } from './';
import Styled from './styles';

export interface ExpansionPanelHeaderProps extends React.ComponentPropsWithRef<'div'> {
  /** Place header content here. Content in the 'collapsed' state would appear here. */
  children: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** @ignore */
  onClick?: React.MouseEventHandler;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
}

/** Container component for the header portion of ExpansionPanel.
 * All additional props will be forwarded to the root element.
 */
const ExpansionPanelHeader = React.forwardRef<HTMLElement, ExpansionPanelHeaderProps>((props: ExpansionPanelHeaderProps, ref) =>
  getContext(({ RTL, theme }) => {
    const { theme: userTheme, ...otherProps } = props;
    const resolvedTheme = _merge({}, theme.EXPANSION_PANEL, userTheme);
    const panelContext = React.useContext(ExpansionPanelContext);

    const handleClick = (event) => {
      const { onClick } = props;
      !panelContext.disabled && onClick && onClick(event);
    };

    const handleKeyDown = (event) => {
      const { onKeyDown } = props;
      !panelContext.disabled && onKeyDown && onKeyDown(event);
    };

    return (
      <Styled.ExpansionPanelHeader
        tabIndex={panelContext.disabled ? -1 : 0}
        disabled={panelContext.disabled}
        aria-selected={panelContext.selected}
        aria-disabled={!!panelContext.disabled}
        $theme={resolvedTheme}
        $rtl={RTL}
        ref={ref}
        $context={panelContext}
        $disableAnimation={panelContext.disableAnimation}
        data-ccfk-expansion-panel-header
        {...otherProps}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      />
    );
  }));

ExpansionPanelHeader.defaultProps = {
  theme: {},
  onClick: undefined,
  onKeyDown: undefined
};

ExpansionPanelHeader.displayName = 'ExpansionPanelHeader';


/* Exports */
export { ExpansionPanelHeader as default };
